import React from 'react'
import './style.scss'

/**
 *
 * Props:
 * @param {number|string} value - Current stock value
 * @param {number|string} max - Total stock capacity
 * @param {number} percent - Direct percentage override (0 - 100)
 * @param {boolean} isLow - Override low stock state (red fill & label)
 * @param {number} lowThreshold - Percentage threshold to mark as low (default: 10)
 * @param {React.ReactNode} label - Custom label element or text
 * @param {boolean} showLabel - Display label above bar (default: true)
 * @param {boolean} isNA - Render N/A text state when stock is unavailable
 * @param {number|string} barWidth - Container width (default: 80)
 * @param {number|string} height - Progress bar height (default: 4)
 * @param {string} className - Additional CSS class names
 * @param {object} style - Custom inline styles
 */
export default function Progress({
  value,
  max,
  percent,
  isLow,
  lowThreshold = 10,
  label,
  showLabel = true,
  isNA = false,
  barWidth = 80,
  height = 4,
  className = '',
  style = {},
  ...restProps
}) {
  const isNotAvailable =
    isNA ||
    value === null ||
    value === undefined ||
    value === 'N/A' ||
    (max === undefined && percent === undefined && value === undefined)

  if (isNotAvailable) {
    return (
      <div
        className={`cb-progress-wrap ${className}`.trim()}
        style={style}
        {...restProps}
      >
        {showLabel && <div className='cb-progress-na'>N/A</div>}
      </div>
    )
  }

  let calculatedPercent = 0
  if (percent !== undefined && percent !== null) {
    calculatedPercent = Math.min(100, Math.max(0, Number(percent)))
  } else if (value !== undefined && max && Number(max) > 0) {
    calculatedPercent = Math.min(
      100,
      Math.max(0, (Number(value) / Number(max)) * 100)
    )
  }

  const lowState =
    isLow !== undefined
      ? isLow
      : calculatedPercent <= lowThreshold

  let formattedLabel = ''
  if (label !== undefined) {
    formattedLabel = label
  } else if (max !== undefined) {
    formattedLabel = `${value} / ${max}`
  } else {
    formattedLabel = `${Math.round(calculatedPercent)}%`
  }

  const widthStyle =
    typeof barWidth === 'number' ? `${barWidth}px` : barWidth
  const heightStyle =
    typeof height === 'number' ? `${height}px` : height

  return (
    <div
      className={`cb-progress-wrap ${className}`.trim()}
      style={{ width: widthStyle, ...style }}
      {...restProps}
    >
      {showLabel && (
        <div className={`cb-progress-label ${lowState ? 'low' : ''}`}>
          {formattedLabel}
        </div>
      )}
      <div className='cb-progress-bar' style={{ height: heightStyle }}>
        <div
          className={`cb-progress-fill ${lowState ? 'low' : ''}`}
          style={{ width: `${calculatedPercent}%` }}
        />
      </div>
    </div>
  )
}
